/**
 * Context React per l'utente autenticato.
 *
 * Chiama GET /api/v1/auth/me dopo il login e espone l'utente corrente
 * a tutti i componenti tramite useAuth().
 *
 * Gestisce anche il logout centralizzato.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { setLanguageFromUser } from '../i18n';

/** Gruppo di appartenenza dell'utente. */
interface GruppoUtente {
  id: string;
  nome: string;
}

/** Ramo operativo (unità organizzativa) disponibile per l'utente. */
export interface UOBrief {
  id: string;
  nome: string;
  tipo: string;
  ruolo: string;
}

/** Modalita' di apertura dei gruppi sidebar (preferenza utente, /auth/me). */
export type MenuMode = 'tutto_aperto' | 'tutto_chiuso' | 'fisarmonica' | 'libera';

/** Profilo dell'utente corrente. */
export interface UtenteCorrente {
  id: string;
  tenant_id: string;
  email: string;
  nome: string;
  cognome: string;
  ruolo: string;
  stato: string;
  ultimo_accesso: string | null;
  telefono: string | null;
  indirizzo: string | null;
  civico: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  data_nascita: string | null;
  codice_fiscale: string | null;
  avatar_documento_id: string | null;
  lingua: string;
  timezone: string;
  gruppi: GruppoUtente[];
  unita_organizzativa_id: string | null;
  unita_organizzative: UOBrief[];
  menu_mode?: MenuMode;
  email_reading_pane?: string;
  email_account_default?: string | null;
  permessi: string[];
}

interface AuthContextValue {
  /** Utente corrente, null se non ancora caricato o non autenticato. */
  user: UtenteCorrente | null;
  /** True durante il fetch iniziale. */
  loading: boolean;
  /** Ricarica il profilo utente dal server. */
  reload: () => Promise<void>;
  /** Esegue il logout: revoca server-side, rimuove token e redirige a /login. */
  logout: () => Promise<void>;
  /** True se l'utente corrente possiede il codice permesso indicato (da /auth/me). */
  hasPermesso: (codice: string) => boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  reload: async () => {},
  logout: async () => {},
  hasPermesso: () => false,
});

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UtenteCorrente | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // DEC-057: il backend accetta cookie httpOnly (preferito) o Bearer header
    // legacy. La chiamata /auth/me funziona in entrambi i casi: se il cookie
    // `jwt` è presente basta, altrimenti il token legacy da localStorage.
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const res = await fetch('/api/v1/auth/me', { headers });
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('token');
          setUser(null);
        }
        return;
      }
      const data: UtenteCorrente = await res.json();
      setUser(data);
      // Allinea la lingua UI alla preferenza dell'utente (Fase 2 i18n).
      setLanguageFromUser(data.lingua);
    } catch {
      /* Errore di rete — manteniamo lo stato corrente. */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    // Revoca server-side (DEC-057): invalida il JTI e cancella il cookie.
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      /* Log network error ma procedi comunque con il logout lato client. */
    }
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/login';
  }, []);

  const permessiSet = useMemo(() => new Set(user?.permessi ?? []), [user]);
  const hasPermesso = useCallback(
    (codice: string) => permessiSet.has(codice),
    [permessiSet]
  );

  return (
    <AuthContext.Provider value={{ user, loading, reload: fetchMe, logout, hasPermesso }}>
      {children}
    </AuthContext.Provider>
  );
};

/** Hook per accedere all'utente corrente e alle azioni di autenticazione. */
const useAuth = (): AuthContextValue => useContext(AuthContext);

export { AuthProvider, useAuth };
